/**
 * Who may interrupt the seller in this browser session — the notifications
 * prompt or the install sheet. One interruption per session, in this order
 * (docs/seller-ux-spec-v1.md §6.14 "First run"):
 *
 * 1. Session 1 belongs to the notifications prompt. The install sheet never
 *    shows.
 * 2. From session 2 on, while notification permission is undecided, the
 *    notifications prompt asks again each session and the install sheet waits.
 * 3. Cap: a seller who keeps tapping "لاحقًا" would never see the install
 *    sheet, so the FIRST session from the third on in which the install sheet
 *    could open (phone width, not already installed, the seller has used the
 *    products screen) and has never been shown is claimed for it. The
 *    notifications prompt stands aside for that one session and resumes after.
 *    The claim is stored, so it happens once — a device that then cannot offer
 *    install loses one notification ask, never all of them.
 *
 * Once permission is decided there is nothing to cap; the install sheet's own
 * weekly throttle applies.
 *
 * 4. The install sheet waits for a seller who has a reason to install
 *    (roundtable 2, B7): her first DELIVERED order, or her fifth session —
 *    whichever comes first. Before that it never opens, cap or not: Mariam met
 *    it in the middle of adding her first products.
 */

/** localStorage: timestamps of install-sheet shows (owned by InstallPrompt). */
export const INSTALL_SHOWN_KEY = "seller_pwa_modal_shown_at_v1";
/** localStorage: set once the seller has used the products screen. */
export const PWA_ENGAGEMENT_KEY = "seller_pwa_engagement_v1";
const SESSION_COUNT_KEY = "seller_session_count_v1";
const SESSION_ORDINAL_KEY = "seller_session_ordinal_v1";
const INSTALL_CAP_SESSION_KEY = "seller_install_cap_session_v1";

/** The session from which the install sheet may claim a session. */
export const INSTALL_CAP_FROM_SESSION = 3;
/** localStorage: set once the store has a delivered order (AppShell). */
export const FIRST_DELIVERED_KEY = "seller_first_delivered_v1";
/** Without a delivered order, the install sheet waits for this session. */
export const INSTALL_MIN_SESSION = 5;

/** Remember that this store has delivered an order (rule 4). */
export function markFirstDelivered(): void {
  try {
    window.localStorage.setItem(FIRST_DELIVERED_KEY, "true");
  } catch {
    /* private mode */
  }
}

/** Rule 4: the first delivered order, or the fifth session. */
export function installMilestoneReached(): boolean {
  try {
    if (window.localStorage.getItem(FIRST_DELIVERED_KEY) === "true") return true;
  } catch {
    /* fall through to the session count */
  }
  return sessionOrdinal() >= INSTALL_MIN_SESSION;
}

/** 1-based count of this browser's sessions (a session = a tab's lifetime). */
export function sessionOrdinal(): number {
  try {
    const known = window.sessionStorage.getItem(SESSION_ORDINAL_KEY);
    if (known) return Number(known) || 1;
    const next = (Number(window.localStorage.getItem(SESSION_COUNT_KEY)) || 0) + 1;
    window.localStorage.setItem(SESSION_COUNT_KEY, String(next));
    window.sessionStorage.setItem(SESSION_ORDINAL_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

function installEverShown(): boolean {
  try {
    const raw = window.localStorage.getItem(INSTALL_SHOWN_KEY);
    return raw !== null && (JSON.parse(raw) as unknown[]).length > 0;
  } catch {
    return false;
  }
}

function notificationPermissionUndecided(): boolean {
  return typeof Notification !== "undefined" && Notification.permission === "default";
}

function installSheetCouldOpen(): boolean {
  try {
    if (window.localStorage.getItem(PWA_ENGAGEMENT_KEY) !== "true") return false;
    if (!installMilestoneReached()) return false;
    if (typeof window.matchMedia !== "function") return false;
    // The install sheet is phone-only (`lg:hidden`, lg = 1024px).
    if (!window.matchMedia("(max-width: 1023.98px)").matches) return false;
    return !window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

/**
 * True when THIS session is the one the install sheet claimed (rule 3).
 * Stable for the whole session, so the prompt and the sheet agree.
 */
export function isInstallCapSession(): boolean {
  try {
    const ordinal = sessionOrdinal();
    const claimed = window.localStorage.getItem(INSTALL_CAP_SESSION_KEY);
    if (claimed !== null) return Number(claimed) === ordinal;
    if (ordinal < INSTALL_CAP_FROM_SESSION) return false;
    if (installEverShown() || !notificationPermissionUndecided()) return false;
    if (!installSheetCouldOpen()) return false;
    window.localStorage.setItem(INSTALL_CAP_SESSION_KEY, String(ordinal));
    return true;
  } catch {
    return false;
  }
}
